<template>
  <DialogRoot v-bind="forwarded">
    <DialogPortal :disabled="disablePortal">
      <DialogOverlay :class="styles.overlay" :style="{ zIndex: zIndex }">
        <DialogContent
          @pointer-down-outside="onClickOutside"
          :class="[
            styles.content,
            styles[size],
            contentClass,
            { [styles.compactClose]: compactCloseIcon },
          ]"
          :style="{ maxWidth: maxWidth, minWidth: minWidth }"
        >
          <div :class="styles.header" v-if="headerTitle || !hideCloseIcon || $slots.icon">
            <div :class="styles.titleWrapper">
              <DialogTitle v-if="headerTitle && !title && !$slots.title" as-child>
                <typography as="span" variant="h6">{{ headerTitle }}</typography>
              </DialogTitle>
              <typography v-else-if="headerTitle" as="span" variant="h6">{{
                headerTitle
              }}</typography>
              <typography v-if="headerTitleSub" variant="body-3" :class="styles.headerTitleSub">{{
                headerTitleSub
              }}</typography>
            </div>

            <div v-if="$slots.icon" :class="styles.icon">
              <slot name="icon" />
            </div>

            <DialogClose v-if="!hideCloseIcon" :class="styles.close" @click="closeModal">
              <CloseIcon />
            </DialogClose>
          </div>

          <DialogTitle v-if="$slots.title || title" as-child>
            <slot name="title">
              <typography :class="styles.text" as="div" variant="h7">{{ title }}</typography>
            </slot>
          </DialogTitle>

          <DialogDescription v-if="$slots.description || description" as-child>
            <slot name="description">
              <typography :class="[styles.text, styles.description]" as="div" variant="body-3">
                {{ description }}
              </typography>
            </slot>
          </DialogDescription>

          <div :class="styles.body">
            <slot :close="closeModal" />
          </div>

          <div v-if="$slots.actions" :class="styles.actions">
            <slot :close="closeModal" name="actions" />
          </div>
        </DialogContent>
      </DialogOverlay>
    </DialogPortal>
  </DialogRoot>
</template>

<script setup lang="ts">
import { reactiveOmit } from '@vueuse/core';
import type { DialogRootEmits, DialogRootProps } from 'radix-vue';
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  useForwardPropsEmits,
} from 'radix-vue';

import CloseIcon from 'shared/icons/close.svg';

import styles from './styles.module.css';

export interface ModalProps extends DialogRootProps {
  contentClass?: string;
  title?: string;
  description?: string;
  /** By default, Modal renders inside the <body>, but this property can disable
   * the behaviour and render the Modal right where it is called. */
  disablePortal?: boolean;
  hideCloseIcon?: boolean;
  headerTitle?: string;
  headerTitleSub?: string;
  maxWidth?: string;
  minWidth?: string;
  closeOnClickOutside?: boolean;
  size?: 's' | 'm' | 'l';
  zIndex?: number;
  compactCloseIcon?: boolean;
}

const props = withDefaults(defineProps<ModalProps>(), {
  hideCloseIcon: false,
  disablePortal: false,
  closeOnClickOutside: true,
  size: 's',
});
const emit = defineEmits<DialogRootEmits>();

const dialogRootProps = reactiveOmit(
  props,
  'contentClass',
  'disablePortal',
  'title',
  'description',
  'maxWidth',
  'minWidth',
  'hideCloseIcon',
  'closeOnClickOutside',
  'size',
  'headerTitle',
  'headerTitleSub',
  'compactCloseIcon',
);
const forwarded = useForwardPropsEmits(dialogRootProps, emit);

defineSlots<{
  default(props: { close: VoidFunction }): never;
  title(props: object): never;
  description(props: object): never;
  actions(props: { close: VoidFunction }): never;
  icon(props: object): never;
}>();

const closeModal = (): void => {
  emit('update:open', false);
};

const onClickOutside = (event: Event): void => {
  if (!props.closeOnClickOutside) {
    event.preventDefault();
  }
};
</script>
